import React, { useEffect, useState } from 'react';
import { Card, Title } from "@tremor/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import Cookies from 'js-cookie';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getSession, getOrDeleteRequest } from '../../utils/utils';
import Swal from 'sweetalert2';
import MainThemeLayout from '../../layout/MainThemeLayout';
import ChartComponent from '../../components/ChartComponent/ChartComponent';
import Map from '../../components/Map/Map';
import DataSelector from '../../components/DataSelector';

const Dashboard = ({ load }) => {
    const [user, setUser] = useState({});
    const [dashboardData, setDashboardData] = useState({});
    const [dataIndicators, setDataIndicators] = useState([]);
    const [resultsGrouped, setResultsGrouped] = useState({});
    const [selectedValue, setSelectedValue] = useState([]);
    const [filteredData, setFilteredData] = useState({});
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { idProject, idCompany } = useParams();
    const { location } = window;
    const [searchParams] = useSearchParams();

    const dateSelect = searchParams.get('date_select');
    const dateStart = searchParams.get('date_start');
    const dateEnd = searchParams.get('date_end');
    const dateType = searchParams.get('date_type') || 'single';
    const sectorId = searchParams.get('sector_id');
    const companyTypeId = searchParams.get('company_type_id');
    const stateId = searchParams.get('state_id');
    const cityId = searchParams.get('city_id');
    const districtId = searchParams.get('district_id');

    const buildQueryParams = () => {
        const params = new URLSearchParams();

        const session = getSession();

        if (location.pathname.startsWith("/PanelDeControlEmpresa/") && idCompany) return;

        if (idProject) params.append('project_id', idProject);

        if (dateType === 'single' && dateSelect) {
            params.append('date_select', `${dateSelect}-01`);
        } else if (dateType === 'range') {
            if (dateStart) params.append('date_start', `${dateStart}-01`);
            if (dateEnd) params.append('date_end', `${dateEnd}-01`);
        }

        if (session && [1, 6].includes(session.type_id)) {
            if (sectorId && sectorId.trim() !== '') params.append('sector_id', sectorId);
            if (companyTypeId && companyTypeId.trim() !== '') params.append('company_type_id', companyTypeId);
            if (stateId && stateId.trim() !== '') params.append('state_id', stateId);
            if (cityId && cityId.trim() !== '') params.append('city_id', cityId);
            if (districtId && districtId.trim() !== '') params.append('district_id', districtId);
        }

        return params.toString();
    };

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const session = getSession();
            if (!session) return;

            setUser(session);

            let baseUrl = [1, 6].includes(session.type_id)
                ? 'dashboard/admin'
                : `dashboard/user/${session.type_id === 4 ? session.id : session.company.user_id}`;

            // const queryParams = buildQueryParams();
            // const url = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;

            const response = await getOrDeleteRequest(baseUrl, 'GET');

            if (response.type === 'success') {
                setDashboardData(response);

                await fetchIndicatorData(session);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            // Swal.fire('Error', 'No se pudieron cargar los datos del dashboard', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchIndicatorData = async (session) => {
        let indicatorUrl = [1, 6].includes(session.type_id)
            ? `dashboard/indicator-data${idCompany ? `?company_id=${idCompany}` : ``}`
            : `dashboard/indicators-by-company/${session.type_id === 4 ? session.id : session.company.user_id}`;

        if (idProject) {
            indicatorUrl += `?project_id=${idProject}`;
        }

        const queryParams = buildQueryParams();
        if (queryParams) {
            indicatorUrl += idProject ? `&${queryParams}` : `?${queryParams}`;
        }

        // console.log('indicatorUrl', indicatorUrl);

        const response = await getOrDeleteRequest(indicatorUrl, 'GET');

        // console.log('Response from indicator data:', response);

        if (response.type === 'success') {
            setDataIndicators(response.result_of_formula_indicators || []);
            setResultsGrouped(response.data_indicators || response.data || {});

            let dashboardData = JSON.parse(localStorage.getItem('dashboardData')) || {};
            let rightData = dashboardData.right || {};
            localStorage.setItem('dashboardData', JSON.stringify({
                right: rightData,
                central: response.data_indicators || response.data || {},
            }));
        }
    };

    useEffect(() => {
        if (!load) return;
        fetchDashboardData();
        fetchIndicatorData(getSession());
    }, [idCompany, dateSelect, dateStart, dateEnd, dateType, sectorId, companyTypeId, stateId, cityId, districtId, searchParams, idProject]);

    useEffect(() => {
        if (selectedValue.length > 0 && resultsGrouped) {
            // Filtrar los datos basados en la selección
            const filtered = {};
            if (Array.isArray(selectedValue)) {
                // Selección múltiple
                selectedValue.forEach(key => {
                    if (resultsGrouped[key]) {
                        filtered[key] = resultsGrouped[key];
                    }
                });
            } else {
                // Selección única
                if (resultsGrouped[selectedValue]) {
                    filtered[selectedValue] = resultsGrouped[selectedValue];
                }
            }
            setFilteredData(filtered);
        } else {
            // Si no hay selección, mostrar todos los datos
            setFilteredData(resultsGrouped);
        }
    }, [selectedValue, resultsGrouped]);

    const StatCard = ({ title, value, icon }) => (
        <div className="w-full lg:w-1/3 p-2">
            <div className="w-full h-full bg-white dark:bg-[#292929] rounded-md p-4">
                <Card className="w-full h-full text-xs px-0 pt-0 ring-0 flex flex-col justify-between">
                    <div className="flex flex-row justify-between items-center">
                        <Title className="text-sm text-start pb-3 font-medium dark:text-white">
                            {title}
                        </Title>
                        <FontAwesomeIcon icon={icon || faBuilding} className="text-xl text-turquesa" />
                    </div>
                    <div className="flex justify-start items-end mt-auto">
                        <h1 className="text-4xl font-bold dark:text-white">
                            {value ?? 0}
                        </h1>
                    </div>
                </Card>
            </div>
        </div>
    );

    const StatsGrid = ({ children }) => (
        <div className="flex flex-wrap">
            {children}
        </div>
    );

    return (
        <MainThemeLayout cards={[dashboardData.energy_consumption || 0]} data={dataIndicators} load={load}>
            <section className="flex flex-col flex-grow">
                {loading ? (
                    <div className="flex justify-center items-center h-full">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-turquesa"></div>
                    </div>
                ) : (
                    <>
                        {[4, 5].includes(user.type_id) && (
                            <>
                                <div className="h-1/5">
                                    <StatsGrid>
                                        <StatCard
                                            title="Respuestas de indicadores"
                                            value={dashboardData.total_indicators}
                                        />
                                        <StatCard
                                            title="Meses reportados"
                                            value={dashboardData.total_months}
                                        />
                                        <StatCard
                                            title="Respuestas totales"
                                            value={dashboardData.total_responses}
                                        />
                                    </StatsGrid>
                                </div>
                                <div className="mt-5">
                                    <div className="w-full h-full p-2">
                                        <div className="w-full h-full bg-white dark:bg-[#292929] rounded-md p-4">
                                            <Card className="w-full h-full text-xs px-0 pt-0 ring-0">
                                                <Title className="text-sm text-start pb-3 font-medium dark:text-white">
                                                    Filtro por indicador
                                                </Title>
                                                <DataSelector
                                                    rawData={resultsGrouped}
                                                    value={selectedValue}
                                                    onChange={(value) => setSelectedValue(value)}
                                                    label="Selecciona un indicador"
                                                    isMulti={true}
                                                />
                                            </Card>
                                        </div>
                                    </div>
                                </div>
                                <div className='mt-5 p-2 w-full'>
                                    <ChartComponent
                                        rawData={filteredData}
                                        chartType="bar"
                                    />
                                </div>
                            </>
                        )}

                        {[1, 6].includes(user.type_id) && (
                            <>
                                <div className="h-1/5">
                                    <StatsGrid>
                                        <StatCard
                                            title="Organizaciones"
                                            value={dashboardData.active_companies}
                                        />
                                        <StatCard
                                            title="Indicadores"
                                            value={dashboardData.total_indicators}
                                        />
                                        <StatCard
                                            title="Socios"
                                            value={dashboardData.active_partners}
                                        />
                                    </StatsGrid>
                                </div>

                                <div className="mt-5">
                                    <div className="w-full h-full p-2">
                                        <div className="w-full h-full bg-white dark:bg-[#292929] rounded-md p-4">
                                            <Card className="w-full h-full text-xs px-0 pt-0 ring-0">
                                                <Title className="text-sm text-start pb-3 font-medium dark:text-white">
                                                    Mapa de ubicación de organizaciones
                                                </Title>
                                                <Map />
                                            </Card>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mt-5">
                                    <div className="w-full h-full p-2">
                                        <div className="w-full h-full bg-white dark:bg-[#292929] rounded-md p-4">
                                            <Card className="w-full h-full text-xs px-0 pt-0 ring-0">
                                                <Title className="text-sm text-start pb-3 font-medium dark:text-white">
                                                    Filtro por indicador
                                                </Title>
                                                <DataSelector
                                                    rawData={resultsGrouped}
                                                    value={selectedValue}
                                                    onChange={(value) => setSelectedValue(value)}
                                                    label="Selecciona un indicador"
                                                    isMulti={true}
                                                />
                                            </Card>
                                        </div>
                                    </div>
                                </div>
                                <div className='mt-5 p-2'>
                                    <ChartComponent
                                        rawData={filteredData}
                                        chartType="bar"
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
            </section>
        </MainThemeLayout>
    );
};

export default Dashboard;